import React from 'react';

import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { Skeleton } from '@material-ui/lab';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { findObject, getArrayOfIdsOfParentFunctions } from 'screens/admin/FunctionAndGrade/Helpers';
import { CheckRadio } from 'screens/home/tasksList/styles';
import styled from 'styled-components';
import { NewStyledIconButton, StyledBox, Divider1 } from 'styles/common';

import { StyledIcon, UncheckedCircle } from 'constants/icons';
import theme from 'constants/theme';

import { Typography2 } from 'uiToolkit/Typography';

const StyledExpansionIcon = styled(StyledIcon)`
  transform: rotate(-90deg);
`;

const StyledTreeItem = styled(TreeItem)`
  position: relative;
  .MuiTreeItem-group {
    margin-left: 7px;
    padding-left: 18px;
  }
  .MuiTreeItem-label:hover {
    background: transparent !important;
  }
  .MuiTreeItem-label {
    background: transparent !important;
  }
  &:focus > .MuiTreeItem-content .MuiTreeItem-label {
    background: transparent !important;
  }
  .Mui-selected > .MuiTreeItem-content .MuiTreeItem-label {
    background: transparent !important;
  }
  .Mui-selected > .MuiTreeItem-content .MuiTreeItem-label:hover {
    background: transparent !important;
  }
  &::before {
    pointer-events: none;
    content: '';
    position: absolute;
    width: 14px;
    left: -18px;
    top: 28px;
    border-bottom: ${(props) => props.$isChildNode && `1px solid ${theme.palette.borders.primary}`};
  }
  &::after {
    pointer-events: none;
    content: '';
    position: absolute;
    width: 1px;
    left: -18px;
    top: 0;
    height: ${(props) => (!props.$showHalfBorder ? '100%' : '50%')};
    border-left: 1px solid ${theme.palette.borders.primary};
  }
`;

const ChildWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;
const HoverWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem 0rem;
  &:hover {
    color: ${theme.palette.text.blue};
    svg {
      fill: ${theme.palette.text.blue};
    }
  }
`;

const DropdownMenuItem = ({
  name,
  uuid,
  onClick,
  selectedFunction,
  selectedFunctionParentsIds = [],
  selectedFunctions,
  isCohortCreation = false,
  isAccessControl = false,
  isRowClickable = false,
}) => {
  const checked =
    isCohortCreation || isAccessControl
      ? Boolean(selectedFunctions[uuid])
      : selectedFunction === uuid || selectedFunctionParentsIds?.includes(uuid);
  return (
    <StyledBox width={1}>
      <ChildWrapper
        onClick={(e) => {
          if (isRowClickable) {
            e.stopPropagation();
            if (isCohortCreation || isAccessControl) {
              onClick({ uuid, name });
            } else {
              onClick(uuid);
            }
          }
        }}
      >
        <HoverWrapper>
          <Typography2 customColor={checked ? theme.palette.text.blue : theme.palette.text.label}>{name}</Typography2>
          <CheckRadio
            disableRipple
            checked={checked}
            style={{ marginLeft: 'auto' }}
            checkedIcon={<UncheckedCircle size="fixed24px" color={theme.palette.icons.blue2} />}
            icon={<UncheckedCircle size="fixed24px" color={theme.palette.icons.grey2} />}
            hoverColor="textSecondary"
            onClick={(e) => {
              e.stopPropagation();
              if (isCohortCreation || isAccessControl) {
                onClick({ uuid, name });
              } else {
                onClick(uuid);
              }
            }}
          />
        </HoverWrapper>
      </ChildWrapper>
      {isCohortCreation && <Divider1 />}
    </StyledBox>
  );
};

const FunctionTreeItems = ({
  functions = [],
  onOptionClick = () => {},
  selectedFunction,
  selectedFunctionParentsIds = [],
  selectedFunctions,
  isCohortCreation = false,
  $isChildNode = false,
  isAccessControl = false,
  isRowClickable = false,
}) => (
  <>
    {functions.map((func, index) => {
      if (func.childFunctions) {
        return (
          <StyledTreeItem
            nodeId={func.function.uuid}
            label={
              <DropdownMenuItem
                name={func.function.name}
                uuid={func.function.uuid}
                onClick={onOptionClick}
                selectedFunction={selectedFunction}
                selectedFunctionParentsIds={selectedFunctionParentsIds}
                selectedFunctions={selectedFunctions}
                isCohortCreation={isCohortCreation}
                isAccessControl={isAccessControl}
                isRowClickable={isRowClickable}
              />
            }
            onClick={(e) => e.stopPropagation()}
            $isChildNode={$isChildNode}
            $showHalfBorder={functions.length - 1 === index}
          >
            <FunctionTreeItems
              functions={func.childFunctions}
              onOptionClick={onOptionClick}
              selectedFunction={selectedFunction}
              selectedFunctionParentsIds={selectedFunctionParentsIds}
              selectedFunctions={selectedFunctions}
              isCohortCreation={isCohortCreation}
              isAccessControl={isAccessControl}
              $isChildNode
              isRowClickable={isRowClickable}
            />
          </StyledTreeItem>
        );
      }
      return (
        <StyledTreeItem
          nodeId={func.function.uuid}
          label={
            <DropdownMenuItem
              name={func.function.name}
              uuid={func.function.uuid}
              onClick={onOptionClick}
              selectedFunction={selectedFunction}
              selectedFunctionParentsIds={selectedFunctionParentsIds}
              selectedFunctions={selectedFunctions}
              isCohortCreation={isCohortCreation}
              isAccessControl={isAccessControl}
              isRowClickable={isRowClickable}
            />
          }
          $isChildNode={$isChildNode}
          $showHalfBorder={functions.length - 1 === index}
          onClick={(e) => e.stopPropagation()}
        />
      );
    })}
  </>
);

const TreeViewComponent = ({
  functions,
  setSelectedFunction,
  selectedFunction,
  loadingFunctions,
  setIsOpen,
  isDropdownOnly = false,
  isCohortCreation = false,
  selectedFunctions = {},
  isAccessControl = false,
  isRowClickable = false,
}) => {
  const jobFunctionObject = findObject(functions ?? [], String(selectedFunction));

  const selectedFunctionParentsIds = getArrayOfIdsOfParentFunctions(functions, jobFunctionObject)?.map(
    (selectedParent) => selectedParent.uuid
  );
  if (loadingFunctions) {
    return <Skeleton />;
  }

  const onFunctionSelect = (value) => {
    setSelectedFunction(value);
    if (!isCohortCreation && !isAccessControl && setIsOpen) {
      setIsOpen(false);
    }
  };

  return (
    <TreeView
      defaultCollapseIcon={
        <NewStyledIconButton padding="none" height="max-content" color="primary">
          <StyledIcon size="header">
            <ExpandMoreRoundedIcon />
          </StyledIcon>
        </NewStyledIconButton>
      }
      defaultExpandIcon={
        <NewStyledIconButton padding="none" height="max-content" color="primary">
          <StyledExpansionIcon size="header">
            <ExpandMoreRoundedIcon />
          </StyledExpansionIcon>
        </NewStyledIconButton>
      }
      style={{
        minWidth: !(isDropdownOnly || isCohortCreation || isAccessControl) ? '14rem' : 'auto',
      }}
    >
      {!(isDropdownOnly || isCohortCreation) && (
        <StyledTreeItem
          nodeId={null}
          label={
            <DropdownMenuItem
              name="All Functions"
              uuid={null}
              onClick={onFunctionSelect}
              selectedFunction={selectedFunction}
              selectedFunctionParentsIds={selectedFunctionParentsIds}
              selectedFunctions={selectedFunctions}
              isCohortCreation={isCohortCreation}
              isAccessControl={isAccessControl}
            />
          }
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <FunctionTreeItems
        functions={functions}
        onOptionClick={onFunctionSelect}
        selectedFunction={selectedFunction}
        selectedFunctionParentsIds={selectedFunctionParentsIds}
        selectedFunctions={selectedFunctions}
        isCohortCreation={isCohortCreation}
        isAccessControl={isAccessControl}
        isRowClickable={isRowClickable}
      />
    </TreeView>
  );
};

export default TreeViewComponent;
